import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { useSelection } from '../selection';
import BulkMenu, { type BulkOption } from './BulkMenu';
import VendorTag from './VendorTag';
import Priority from './Priority';
import Icon from './Icon';
import { confirmDialog } from '../confirm';
import { displayName, initials, isExternalVendor } from '../people';
import { boardAssignees } from '../projects';
import { boardColumnsFor } from '../lifecycle';
import {
  STATUS_LABELS,
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  liveAreas,
  type Issue,
  type IssueStatus,
  type IssuePriority,
} from '../types';

type Verb = 'move' | 'assign' | 'area' | 'epic' | 'priority';

/** How long the result toast sits before it steps back to the bar (or to nothing).
 *  Long enough to read the result and reach for Undo; the countdown also PAUSES
 *  while the toast is hovered or focused, so it can never vanish under the cursor. */
const RESULT_MS = 12000;

interface ResultState {
  lead: string;
  /** Cards a partial move could not take — kept selected, reachable via "Show it". */
  refused: string[];
  /** The one reversal of this one batch (one audit line = one Undo). */
  undo?: () => void | Promise<void>;
  /** Whether a digest was actually sent — only then does the toast say "notified once". */
  notified: boolean;
}

/**
 * THE BULK ACTION BAR — the floating bar that appears when ≥1 card is selected
 * (project-bulk-card-changes). It is VIEW-AGNOSTIC: it reads the selection and the
 * board context from the store and drives the store's bulk actions, so the Grid
 * view (#508) mounts this exact component and gets bulk edit for free — it is not
 * wired to Board.tsx's internals.
 *
 * The bar docks bottom-centre of the board area and slides in on the first
 * selection (R7). Its verbs reuse the board's `.tb-btn` menu-trigger (R3); Delete
 * is the one red verb and opens the app's own destructive confirm, never
 * window.confirm (R24). Each verb opens a <BulkMenu>; Assign-to confines vendors to
 * the boards they belong to, showing — never dropping — a locked vendor with the
 * reason (R15/R28). After an action the bar is REPLACED IN PLACE by a result toast
 * that says what happened and offers one Undo; a partial move keeps the refused
 * cards selected and can filter to them.
 */
export default function BulkBar({ onShowRefused }: { onShowRefused: (ids: string[]) => void }) {
  const ids = useSelection((s) => s.ids);
  const clearSel = useSelection((s) => s.clear);
  const exitSelect = useSelection((s) => s.exitSelect);
  const onlySel = useSelection((s) => s.only);

  const issues = useApp((s) => s.issues);
  const areas = useApp((s) => s.areas);
  const meta = useApp((s) => s.meta);
  const profiles = useApp((s) => s.profiles);
  const access = useApp((s) => s.access);
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const groupsOf = useApp((s) => s.groupsOf);
  const me = useApp((s) => s.user?.email ?? '');

  const bulkMove = useApp((s) => s.bulkMove);
  const bulkAssign = useApp((s) => s.bulkAssign);
  const bulkPatch = useApp((s) => s.bulkPatch);
  const bulkDuplicate = useApp((s) => s.bulkDuplicate);
  const bulkDelete = useApp((s) => s.bulkDelete);
  const bulkRestore = useApp((s) => s.bulkRestore);
  const restoreIssues = useApp((s) => s.restoreIssues);

  const [openVerb, setOpenVerb] = useState<Verb | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);
  const [busy, setBusy] = useState(false);
  // The result toast's auto-dismiss PAUSES while the toast is hovered or focused,
  // so it can never slip away as a person reaches for Undo.
  const [paused, setPaused] = useState(false);

  const btn = {
    move: useRef<HTMLButtonElement>(null),
    assign: useRef<HTMLButtonElement>(null),
    area: useRef<HTMLButtonElement>(null),
    epic: useRef<HTMLButtonElement>(null),
    priority: useRef<HTMLButtonElement>(null),
  };

  const selected = useMemo(() => issues.filter((i) => ids.has(i.id)), [issues, ids]);
  const n = selected.length;
  const noun = `${n} card${n === 1 ? '' : 's'}`;

  // The result toast steps away on its own after a while — but not while it is
  // hovered or focused (paused), so the countdown restarts when the pointer leaves.
  useEffect(() => {
    if (!result || paused) return;
    const t = setTimeout(() => setResult(null), RESULT_MS);
    return () => clearTimeout(t);
  }, [result, paused]);

  // Escape dismisses the result toast (when no verb menu is open — the menu owns
  // Escape first, in capture). Leaving select MODE on Escape is the Board's own
  // handler, so it works whether or not anything is selected.
  useEffect(() => {
    if (!result) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || openVerb) return;
      setResult(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [result, openVerb]);

  if (n === 0 && !result) return null;

  const close = () => setOpenVerb(null);

  /* ── The verb option lists ───────────────────────────────────────────────── */
  const statusOptions: BulkOption[] = [...boardColumnsFor(meta), 'wontdo' as IssueStatus].map((s) => ({
    value: s,
    label: STATUS_LABELS[s],
    mark: <span className={`status-dot dot-${s}`} aria-hidden="true" />,
  }));

  const areaOptions: BulkOption[] = [
    { value: '', label: 'No area' },
    ...liveAreas(areas).map((a) => ({
      value: a.id,
      label: a.name,
      mark: <span className="bulk-swatch" style={{ background: a.colour }} aria-hidden="true" />,
    })),
  ];

  const epicNames = [...new Set(issues.map((i) => i.epic).filter((e): e is string => !!e && e.trim() !== ''))].sort();
  const epicOptions: BulkOption[] = [
    { value: '', label: 'No epic' },
    ...epicNames.map((e) => ({ value: e, label: e })),
  ];

  const priorityOptions: BulkOption[] = PRIORITY_ORDER.map((p) => ({
    value: p,
    label: PRIORITY_LABELS[p],
    mark: <Priority priority={p} markOnly />,
  }));

  // Assign-to — vendor confinement made visible (requirement #3). On this one board
  // everyone who can open it is assignable (internal first, then its own vendors);
  // a vendor who belongs to another board is SHOWN but LOCKED with the reason and
  // the fix, never silently dropped.
  const project = projects.find((p) => p.id === projectId);
  const boardName = project?.name ?? 'this board';
  const members = boardAssignees(project, access.emails, access.admins, groupsOf);
  const memberSet = new Set(members.map((e) => e.toLowerCase()));
  const internal = members.filter((e) => !isExternalVendor(e, profiles));
  const boardVendors = members.filter((e) => isExternalVendor(e, profiles));
  const otherVendors = access.emails.filter(
    (e) => isExternalVendor(e, profiles) && !memberSet.has(e.toLowerCase()),
  );
  const av = (e: string) => <span className="bulk-av" aria-hidden="true">{initials(e, profiles)}</span>;
  const assignOptions: BulkOption[] = [
    ...internal.map((e) => ({
      value: e,
      label: displayName(e, profiles),
      mark: av(e),
      meta: e.toLowerCase() === me.toLowerCase() ? 'you' : undefined,
    })),
    ...boardVendors.map((e) => ({
      value: e,
      label: displayName(e, profiles),
      group: 'Vendors',
      mark: av(e),
      trailing: <VendorTag who={e} profiles={profiles} compact />,
      meta: boardName,
    })),
    ...otherVendors.map((e) => ({
      value: e,
      label: displayName(e, profiles),
      group: 'Vendors',
      mark: av(e),
      trailing: <VendorTag who={e} profiles={profiles} compact />,
      locked: true,
      lockNote: (
        <>Not a vendor of <b>{boardName}</b> — invite them to the board first</>
      ),
    })),
  ];

  /* ── Applying a verb ─────────────────────────────────────────────────────── */
  const after = (r: ResultState, keep: string[] = []) => {
    if (keep.length) onlySel(keep);
    else clearSel();
    setResult(r);
    close();
  };

  const doMove = async (status: IssueStatus) => {
    if (busy) return;
    setBusy(true);
    const before = new Map(selected.map((i) => [i.id, i.status]));
    const targetIds = selected.map((i) => i.id);
    const { moved, refused, notified } = await bulkMove(targetIds, status, `Moved to ${STATUS_LABELS[status]}`);
    setBusy(false);
    const undo =
      moved.length > 0
        ? () => bulkRestore(moved.map((id) => ({ id, patch: { status: before.get(id) } })), 'Reverted the move')
        : undefined;
    const lead = `${moved.length} card${moved.length === 1 ? '' : 's'} moved to ${STATUS_LABELS[status]}`;
    after({ lead, refused, undo, notified }, refused);
  };

  const doAssign = async (who: string) => {
    if (busy) return;
    setBusy(true);
    const before = selected.map((i) => ({ id: i.id, patch: { responsible: i.responsible ?? '' } }));
    const targetIds = selected.map((i) => i.id);
    const { notified } = await bulkAssign(targetIds, who, `Assigned to ${displayName(who, profiles)}`);
    setBusy(false);
    after({
      lead: `${displayName(who, profiles)} assigned to ${noun}`,
      refused: [],
      undo: () => bulkRestore(before, 'Reverted the assignment'),
      notified,
    });
  };

  const doField = (field: 'area' | 'epic' | 'priority', value: string, word: string, lead: string) => {
    if (busy) return;
    const before = selected.map((i) => ({ id: i.id, patch: { [field]: i[field] ?? (field === 'area' ? null : '') } as Partial<Issue> }));
    const patch: Partial<Issue> =
      field === 'area'
        ? { area: value || null }
        : field === 'priority'
          ? { priority: value as IssuePriority }
          : { epic: value };
    // The audit line is the plain verb — describeBulk appends "on N issues (#…)".
    bulkPatch(selected.map((i) => i.id), patch, word);
    after({
      lead: `${lead} on ${noun}`,
      refused: [],
      undo: () => bulkRestore(before, `Reverted the ${field} change`),
      notified: false,
    });
  };

  const doDuplicate = async () => {
    if (busy) return;
    setBusy(true);
    const targetIds = selected.map((i) => i.id);
    await bulkDuplicate(targetIds, 'Duplicated');
    setBusy(false);
    after({ lead: `${noun} duplicated`, refused: [], notified: false });
  };

  const doDelete = async () => {
    if (busy) return;
    close();
    const ok = await confirmDialog({
      title: `Delete ${noun}?`,
      body: 'The cards are removed from the board. Each can be put back from the History trail.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    const gone = await bulkDelete(selected.map((i) => i.id), 'Deleted');
    setBusy(false);
    after({
      lead: `${gone.length} card${gone.length === 1 ? '' : 's'} deleted`,
      refused: [],
      undo: () => restoreIssues(gone, `Restored ${gone.length} card${gone.length === 1 ? '' : 's'}`),
      notified: false,
    });
  };

  const pick = (verb: Verb) => (value: string) => {
    if (verb === 'move') void doMove(value as IssueStatus);
    else if (verb === 'assign') void doAssign(value);
    else if (verb === 'area') doField('area', value, 'Set the area', 'Area set');
    else if (verb === 'epic') doField('epic', value, 'Set the epic', 'Epic set');
    else if (verb === 'priority') doField('priority', value, 'Set the priority', 'Priority set');
  };

  const menuTitle: Record<Verb, string> = {
    move: `Move ${noun} to`,
    assign: `Assign ${noun} to`,
    area: `Set the area on ${noun}`,
    epic: `Set the epic on ${noun}`,
    priority: `Set the priority on ${noun}`,
  };
  const menuOptions: Record<Verb, BulkOption[]> = {
    move: statusOptions,
    assign: assignOptions,
    area: areaOptions,
    epic: epicOptions,
    priority: priorityOptions,
  };

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div className="bulk-dock">
      {result ? (
        <div
          className="bulk-toast"
          role="status"
          aria-live="polite"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          <div className="bulk-toast-line">
            <span className="bulk-toast-lead">
              <span className="bulk-ok" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
              </span>
              {result.lead}
            </span>
            {result.refused.length > 0 && (
              <>
                <span className="bulk-toast-sep">·</span>
                <span className="bulk-toast-refused">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 3 2 20h20L12 3zM12 10v4M12 17v.5" /></svg>
                  {result.refused.length} couldn’t — {result.refused.length === 1 ? 'it’s' : 'they’re'} blocked
                </span>
                <button type="button" className="bulk-toast-act" onClick={() => onShowRefused(result.refused)}>Show {result.refused.length === 1 ? 'it' : 'them'}</button>
              </>
            )}
            {result.undo && (
              <>
                <span className="bulk-toast-sep">·</span>
                <button type="button" className="bulk-toast-act" onClick={() => { void result.undo?.(); setResult(null); }}>Undo</button>
              </>
            )}
          </div>
          {result.notified && (
            <div className="bulk-toast-notify">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.5 21a2 2 0 0 1-3 0" /></svg>
              Everyone affected was notified once — one summary, not {n} alerts.
            </div>
          )}
        </div>
      ) : (
        <div className="bulk-bar" role="toolbar" aria-label="Bulk actions">
          <span className="bulk-count">
            <span className="bulk-count-chk" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
            </span>
            {n} selected
          </span>
          <span className="bulk-div" />
          <button ref={btn.move} type="button" className={`tb-btn${openVerb === 'move' ? ' open' : ''}`} aria-haspopup="menu" aria-expanded={openVerb === 'move'} onClick={() => setOpenVerb((v) => (v === 'move' ? null : 'move'))}>
            <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 12h12M13 7l5 5-5 5" /></svg>
            Move to <Icon name="chevron" size={15} className="bulk-chev" />
          </button>
          <button ref={btn.assign} type="button" className={`tb-btn${openVerb === 'assign' ? ' open' : ''}`} aria-haspopup="menu" aria-expanded={openVerb === 'assign'} onClick={() => setOpenVerb((v) => (v === 'assign' ? null : 'assign'))}>
            <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8" r="3.2" /><path d="M5 20c0-3.3 3-5.5 7-5.5s7 2.2 7 5.5" /></svg>
            Assign to <Icon name="chevron" size={15} className="bulk-chev" />
          </button>
          <button ref={btn.area} type="button" className={`tb-btn${openVerb === 'area' ? ' open' : ''}`} aria-haspopup="menu" aria-expanded={openVerb === 'area'} onClick={() => setOpenVerb((v) => (v === 'area' ? null : 'area'))}>
            <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 7l5-3 6 3 5-2v13l-5 2-6-3-5 3z" /></svg>
            Area <Icon name="chevron" size={15} className="bulk-chev" />
          </button>
          <button ref={btn.epic} type="button" className={`tb-btn${openVerb === 'epic' ? ' open' : ''}`} aria-haspopup="menu" aria-expanded={openVerb === 'epic'} onClick={() => setOpenVerb((v) => (v === 'epic' ? null : 'epic'))}>
            <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /></svg>
            Epic <Icon name="chevron" size={15} className="bulk-chev" />
          </button>
          <button ref={btn.priority} type="button" className={`tb-btn${openVerb === 'priority' ? ' open' : ''}`} aria-haspopup="menu" aria-expanded={openVerb === 'priority'} onClick={() => setOpenVerb((v) => (v === 'priority' ? null : 'priority'))}>
            <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 5v14M7 5 4 8M7 5l3 3M17 19V5M17 19l-3-3M17 19l3-3" /></svg>
            Priority <Icon name="chevron" size={15} className="bulk-chev" />
          </button>
          <span className="bulk-div" />
          <button type="button" className="tb-btn bulk-ghost" onClick={() => void doDuplicate()}>
            <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></svg>
            Duplicate
          </button>
          <button type="button" className="tb-btn bulk-danger" onClick={() => void doDelete()}>
            <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>
            Delete
          </button>
          <span className="bulk-div" />
          <button type="button" className="tb-btn bulk-ghost bulk-clear" title="Done selecting (Esc)" aria-label="Done selecting" onClick={() => exitSelect()}>
            <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
          </button>
        </div>
      )}

      {openVerb && (
        <BulkMenu
          anchorRef={btn[openVerb]}
          title={menuTitle[openVerb]}
          options={menuOptions[openVerb]}
          filter={openVerb === 'assign'}
          footer={
            openVerb === 'assign' && (boardVendors.length > 0 || otherVendors.length > 0) ? (
              <>A vendor is assignable only where they are a vendor of the board.</>
            ) : undefined
          }
          onPick={pick(openVerb)}
          onClose={close}
        />
      )}
    </div>
  );
}
